import { useRef, useState } from 'react';
import PageShell from '@components/pages/PageShell';
import {
    ResourceHero,
    DownloadGrid,
    Pagination,
    SectionLabel,
    TopAndRecent,
    EmptyList,
} from '@components/pages/ResourceSections';
import Newsletter from '@components/landing/Newsletter';
import { collectionOf, DOCUMENTS, DOC_RECENT, DOC_TOP } from '@/data/resources';
import { usePagedList } from '@/lib/usePagedList';
import Seo from '@components/Seo';
import { DocGridSkeleton, TopAndRecentSkeleton, useSiteLoading } from '@components/Skeleton';

/** Three rows of the four-column grid. */
const PER_PAGE = 12;

/**
 * Publications, Infographics and Reports share one design — a dark search hero
 * over a grid of downloadable cards — so they share one component. Reports and
 * Infographics also open with the same top/recent split the blog uses.
 */
export default function Downloads({
    titleLead,
    titleBold,
    intro,
    label,
    basePath,
    topLabel,
    recentLabel,
}: {
    titleLead: string;
    titleBold: string;
    intro: string;
    label: string;
    /** Route this page sits on, used to build shareable card links. */
    basePath: string;
    /** Provide both to show the top/recent split above the grid. */
    topLabel?: string;
    recentLabel?: string;
}) {
    const collection = collectionOf(basePath);
    const loading = useSiteLoading();
    const [query, setQuery] = useState('');
    const grid = useRef<HTMLElement>(null);
    const items = DOCUMENTS[collection];
    const { page, setPage, pages, matches, pageItems } = usePagedList(items, PER_PAGE, query, (d) => d.text);
    const searching = query.trim().length > 0;

    const goTo = (next: number) => {
        setPage(next);
        grid.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <PageShell>
            <Seo title={`${titleLead} ${titleBold}`.trim()} description={intro} />
            <ResourceHero
                titleLead={titleLead}
                titleBold={titleBold}
                intro={intro}
                query={query}
                onSearch={setQuery}
                searchPlaceholder={`Search ${titleBold.toLowerCase()}`}
            />

            {topLabel &&
                recentLabel &&
                !searching &&
                (DOC_TOP[collection].title ? (
                    <TopAndRecent
                        top={DOC_TOP[collection]}
                        recent={DOC_RECENT[collection]}
                        topLabel={topLabel}
                        recentLabel={recentLabel}
                    />
                ) : loading ? (
                    <TopAndRecentSkeleton />
                ) : null)}

            <section
                ref={grid}
                className={`scroll-mt-24 bg-white pb-14 lg:pb-20 ${topLabel && !searching ? '' : 'pt-14 lg:pt-20'}`}
                aria-live="polite"
            >
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <SectionLabel>{searching ? 'Search results' : label}</SectionLabel>
                        <p className="text-[13px] text-[#5e5e5e]">
                            {searching ? `${matches.length} for “${query.trim()}”` : `${items.length} in total`}
                        </p>
                    </div>
                    {/* A grid still on its way is not an empty one: while the feed is
                        in flight this shows the shape of what is coming, so the page
                        never tells a visitor there is nothing here. */}
                    {pageItems.length > 0 ? (
                        <DownloadGrid items={pageItems} basePath={basePath} />
                    ) : loading ? (
                        <DocGridSkeleton count={PER_PAGE} />
                    ) : (
                        <EmptyList query={query} whenSearching={`Nothing matches “${query.trim()}”.`} />
                    )}
                    <Pagination page={page} pages={pages} onChange={goTo} />
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
